import { makeAutoObservable } from "mobx";
import { track } from "../lib/analytics";

export type ToastKind = "info" | "success" | "error";
export type AuthMode = "signIn" | "signUp";
export type ThemeMode = "dark" | "light";

export interface Toast {
	id: number;
	kind: ToastKind;
	message: string;
}

export class UiStore {
	authModalOpen = false;
	authMode: AuthMode = "signIn";
	theme: ThemeMode = "dark";
	mobilePlayModalOpen = false;
	discoveryCollapsed = false;
	discoveryMobileOpen = false;
	playNextCollapsed = false;
	private cataloguePreferencesLoaded = false;
	toasts: Toast[] = [];
	private nextId = 1;
	private timers = new Map<number, ReturnType<typeof setTimeout>>();
	private authRequestResolvers = new Set<(signedIn: boolean) => void>();

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
		if (import.meta.env?.SSR) return;
		let storedTheme: string | null = null;
		try {
			storedTheme = typeof window !== "undefined" ? window.localStorage.getItem("muhaha-theme") : null;
		} catch {
			storedTheme = null;
		}
		const theme = storedTheme ?? (typeof document !== "undefined" ? document.documentElement.dataset?.theme : null);
		this.theme = theme === "light" ? "light" : "dark";
		this.applyTheme();
	}

	loadCataloguePreferences() {
		if (this.cataloguePreferencesLoaded || typeof window === "undefined") return;
		this.cataloguePreferencesLoaded = true;
		try {
			this.discoveryCollapsed = localStorage.getItem("muhaha:sidebar-collapsed") === "true";
		} catch { /* Preferences are optional. */ }
	}

	toggleDiscoverySidebar() {
		this.discoveryCollapsed = !this.discoveryCollapsed;
		try { localStorage.setItem("muhaha:sidebar-collapsed", String(this.discoveryCollapsed)); } catch { /* Optional. */ }
	}

	setDiscoveryMobileOpen(open: boolean) { this.discoveryMobileOpen = open; }

	setPlayNextCollapsed(collapsed: boolean) {
		this.playNextCollapsed = collapsed;
	}

	setTheme(theme: ThemeMode) {
		this.theme = theme;
		this.applyTheme();
		try {
			if (typeof window !== "undefined") window.localStorage.setItem("muhaha-theme", theme);
			if (typeof document !== "undefined") document.cookie = `muhaha-theme=${theme}; Path=/; SameSite=Lax; Max-Age=31536000`;
		} catch {
			// Theme still applies for the current session when storage is unavailable.
		}
	}

	toggleTheme() {
		this.setTheme(this.theme === "dark" ? "light" : "dark");
	}

	private applyTheme() {
		if (typeof document !== "undefined") document.documentElement.dataset.theme = this.theme;
	}

	openAuth() {
		if (!this.authModalOpen) track("auth_modal_opened", {});
		this.authMode = "signIn";
		this.authModalOpen = true;
	}

	openAuthWithMode(mode: AuthMode) {
		if (!this.authModalOpen) track("auth_modal_opened", {});
		this.authMode = mode;
		this.authModalOpen = true;
	}

	setAuthMode(mode: AuthMode) {
		this.authMode = mode;
	}

	requestAuth(): Promise<boolean> {
		this.openAuth();
		return new Promise((resolve) => {
			this.authRequestResolvers.add(resolve);
		});
	}

	closeAuth(signedIn = false) {
		this.authModalOpen = false;
		const resolvers = [...this.authRequestResolvers];
		this.authRequestResolvers.clear();
		for (const resolve of resolvers) resolve(signedIn);
	}

	openMobilePlay() {
		this.mobilePlayModalOpen = true;
	}

	closeMobilePlay() {
		this.mobilePlayModalOpen = false;
	}

	toast(message: string, kind: ToastKind = "info") {
		const id = this.nextId++;
		this.toasts = [...this.toasts, { id, kind, message }];
		const timer = setTimeout(() => this.dismiss(id), 3200);
		this.timers.set(id, timer);
	}

	dismiss(id: number) {
		const timer = this.timers.get(id);
		if (timer) {
			clearTimeout(timer);
			this.timers.delete(id);
		}
		this.toasts = this.toasts.filter((t) => t.id !== id);
	}
}

export const uiStore = new UiStore();
